// Sprint · Slice 7.1 · Governed Ad Creation Wizard (v0).
//
// A five-step flow that mirrors the backend state machine:
//   1. Select ad account
//   2. Compose (campaign + creative)
//   3. Preflight (health-claims sub-check)
//   4. Freeze (SHA-256 payload hash)
//   5. Publish (test_mode default)
//
// Everything is server-authoritative — each step calls a real endpoint
// and reads back the new package state so the UI never diverges from
// the actual audit record.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const STEPS = [
  { key: "account", label: "Ad account" },
  { key: "compose", label: "Compose" },
  { key: "preflight", label: "Preflight" },
  { key: "freeze", label: "Freeze" },
  { key: "publish", label: "Publish" },
];

function StateLabel({ state }) {
  const tone = state === "published" ? "success"
    : state === "frozen" ? "success"
    : state === "preflighted" ? "warn"
    : state === "failed" ? "danger" : "muted";
  return <Pill tone={tone} testId={`pkg-state-${state}`}>{state}</Pill>;
}

function StepBar({ activeIdx }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 24 }}
         data-testid="wizard-step-bar">
      {STEPS.map((s, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <div key={s.key}
               data-testid={`wizard-step-${s.key}`}
               style={{
                 flex: 1, padding: "10px 14px",
                 border: "1px solid var(--bo-border)",
                 background: active ? "var(--bo-panel)" : "var(--bo-panel-alt)",
                 color: active ? "var(--bo-ink)" : (done ? "var(--bo-muted)" : "var(--bo-muted)"),
                 borderTop: active ? "2px solid #c5a25f" : "1px solid var(--bo-border)",
                 fontSize: 12, textTransform: "uppercase", letterSpacing: 1,
                 borderRadius: 6,
               }}>
            <span style={{ fontWeight: 700, marginRight: 6, color: done ? "#2f9b5e" : undefined }}>
              {String(i + 1).padStart(2, "0")}
            </span>
            {s.label}
          </div>
        );
      })}
    </div>
  );
}

function Step1SelectAccount({ accounts, value, onChange, onNext }) {
  return (
    <div className="bo-card" data-testid="wizard-step-1-card">
      <div className="bo-eyebrow">Step 1 · Ad account</div>
      <p style={{ color: "var(--bo-muted)", margin: "8px 0 16px" }}>
        Pick which Meta ad account this campaign will publish into.
        Every ad account defaults to sandbox until the operator explicitly
        turns on live mode.
      </p>
      {accounts.length === 0 ? (
        <div className="bo-card" style={{ background: "var(--bo-panel-alt)" }}>
          No ad accounts bound. <Link to="/brand-os/ads" style={{ color: "var(--bo-accent)" }}>Bind one first →</Link>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {accounts.map((a) => (
            <label key={a.id}
                   data-testid={`wizard-account-option-${a.id}`}
                   className="bo-check"
                   style={{
                     display: "flex", gap: 12, padding: 12,
                     border: value === a.id ? "2px solid #c5a25f" : "1px solid var(--bo-border)",
                     borderRadius: 8, cursor: "pointer",
                     background: value === a.id ? "var(--bo-panel)" : undefined,
                   }}>
              <input type="radio" name="ad-account" value={a.id}
                     checked={value === a.id}
                     onChange={() => onChange(a.id)} />
              <div>
                <div style={{ fontWeight: 700 }}>{a.display_name}</div>
                <div style={{ fontSize: 12, color: "var(--bo-muted)", fontFamily: "monospace" }}>
                  {a.external_ad_account_id} · {a.currency}
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {a.test_mode_default && (
                    <Pill tone="warn" testId={`account-testmode-${a.id}`}>Sandbox default</Pill>
                  )}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <button className="bo-btn bo-btn-primary"
                data-testid="wizard-step-1-next"
                disabled={!value} onClick={onNext}>Next</button>
      </div>
    </div>
  );
}

function Step2Compose({ form, onChange, busy, error, onSubmit, onBack }) {
  return (
    <div className="bo-card" data-testid="wizard-step-2-card">
      <div className="bo-eyebrow">Step 2 · Compose the ad</div>
      <p style={{ color: "var(--bo-muted)", margin: "8px 0 16px" }}>
        This is a draft — no external calls happen until you publish
        in step 5.
      </p>
      {error && <ErrorBanner error={error} testId="wizard-step-2-error" />}

      <div className="bo-field">
        <label className="bo-label">Campaign name</label>
        <input className="bo-input" data-testid="wizard-campaign-name"
               value={form.campaign_name}
               onChange={(e) => onChange({ ...form, campaign_name: e.target.value })}
               placeholder="e.g. Precipice · US veterans · Fall '26 leads" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="bo-field">
          <label className="bo-label">Objective</label>
          <select className="bo-input" data-testid="wizard-objective"
                  value={form.objective}
                  onChange={(e) => onChange({ ...form, objective: e.target.value })}>
            <option value="OUTCOME_LEADS">Leads (OUTCOME_LEADS)</option>
            <option value="OUTCOME_ENGAGEMENT">Engagement</option>
            <option value="OUTCOME_AWARENESS">Awareness</option>
            <option value="OUTCOME_TRAFFIC">Traffic</option>
          </select>
        </div>
        <div className="bo-field">
          <label className="bo-label">Daily budget (USD)</label>
          <input type="number" step="0.01" min="0.01" className="bo-input"
                 data-testid="wizard-daily-budget"
                 value={form.daily_budget_usd}
                 onChange={(e) => onChange({ ...form, daily_budget_usd: e.target.value })} />
          <div className="bo-hint">
            Global daily spend cap is <strong>$25.00</strong>. Anything higher
            gets blocked by the governor before Meta is called.
          </div>
        </div>
      </div>
      <div className="bo-field">
        <label className="bo-label">Headline (short)</label>
        <input className="bo-input" data-testid="wizard-headline"
               value={form.headline}
               onChange={(e) => onChange({ ...form, headline: e.target.value })}
               placeholder="e.g. Made with care." />
      </div>
      <div className="bo-field">
        <label className="bo-label">Ad copy</label>
        <textarea rows={4} className="bo-input" data-testid="wizard-ad-copy"
                  value={form.ad_copy}
                  onChange={(e) => onChange({ ...form, ad_copy: e.target.value })}
                  placeholder="What the ad says. Preflight will refuse copy that makes an unapproved medical claim." />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="bo-field">
          <label className="bo-label">CTA</label>
          <select className="bo-input" data-testid="wizard-cta"
                  value={form.cta}
                  onChange={(e) => onChange({ ...form, cta: e.target.value })}>
            <option value="LEARN_MORE">Learn more</option>
            <option value="SIGN_UP">Sign up</option>
            <option value="CONTACT_US">Contact us</option>
            <option value="GET_QUOTE">Get a quote</option>
          </select>
        </div>
        <div className="bo-field">
          <label className="bo-label">Product ID (optional)</label>
          <input className="bo-input" data-testid="wizard-product-id"
                 value={form.product_id}
                 onChange={(e) => onChange({ ...form, product_id: e.target.value })}
                 placeholder="Ties the spend to a per-product cap" />
        </div>
      </div>

      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between" }}>
        <button className="bo-btn bo-btn-ghost" data-testid="wizard-step-2-back"
                onClick={onBack}>Back</button>
        <button className="bo-btn bo-btn-primary" data-testid="wizard-step-2-next"
                disabled={busy} onClick={onSubmit}>
          {busy ? "Saving draft…" : "Save draft & preflight"}
        </button>
      </div>
    </div>
  );
}

function Step3Preflight({ pkg, busy, error, onRun, onBack, onNext }) {
  const hc = (pkg?.preflight_result || {}).health_claims;
  return (
    <div className="bo-card" data-testid="wizard-step-3-card">
      <div className="bo-eyebrow">Step 3 · Health-claims preflight</div>
      <p style={{ color: "var(--bo-muted)", margin: "8px 0 16px" }}>
        Deterministic sub-check that refuses ad copy Meta prohibits for
        wellness/medical advertising. Runs entirely on our side, in
        milliseconds, with $0 LLM cost.
      </p>
      {error && <ErrorBanner error={error} testId="wizard-step-3-error" />}

      {hc ? (
        <div className="bo-card" data-testid="preflight-result"
             style={{ background: "var(--bo-panel-alt)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontFamily: "var(--bo-serif)", fontSize: 18 }}
                 data-testid="preflight-severity">
              Severity:{" "}
              <span style={{
                color: hc.severity === "BLOCK" ? "#b45309"
                  : hc.severity === "WARN" ? "#8a5a0a" : "#2f9b5e",
              }}>{hc.severity}</span>
            </div>
            <Pill tone={hc.severity === "BLOCK" ? "danger"
                          : hc.severity === "WARN" ? "warn" : "success"}
                  testId="preflight-code">
              {hc.code}
            </Pill>
          </div>
          <p style={{ marginTop: 8 }}>{hc.message}</p>
          {(hc.disease_hits?.length > 0) && (
            <div style={{ fontSize: 13 }} data-testid="preflight-disease-hits">
              <strong>Disease/condition claims:</strong> {hc.disease_hits.join(", ")}
            </div>
          )}
          {(hc.outcome_hits?.length > 0) && (
            <div style={{ fontSize: 13 }} data-testid="preflight-outcome-hits">
              <strong>Outcome guarantees:</strong> {hc.outcome_hits.length}
            </div>
          )}
          {(hc.sensitive_hits?.length > 0) && (
            <div style={{ fontSize: 13 }} data-testid="preflight-sensitive-hits">
              <strong>Sensitive topics:</strong> {hc.sensitive_hits.join(", ")}
            </div>
          )}
        </div>
      ) : (
        <p style={{ color: "var(--bo-muted)" }}>Preflight has not run yet.</p>
      )}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between" }}>
        <button className="bo-btn bo-btn-ghost" data-testid="wizard-step-3-back"
                onClick={onBack}>Back</button>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="bo-btn bo-btn-ghost" data-testid="wizard-step-3-rerun"
                  disabled={busy} onClick={onRun}>
            {busy ? "Running…" : "Re-run preflight"}
          </button>
          <button className="bo-btn bo-btn-primary" data-testid="wizard-step-3-next"
                  disabled={!hc || hc.severity === "BLOCK"} onClick={onNext}>
            Freeze package
          </button>
        </div>
      </div>
    </div>
  );
}

function Step4Freeze({ pkg, busy, error, onFreeze, onBack, onNext }) {
  return (
    <div className="bo-card" data-testid="wizard-step-4-card">
      <div className="bo-eyebrow">Step 4 · Freeze the audit package</div>
      <p style={{ color: "var(--bo-muted)", margin: "8px 0 16px" }}>
        Freezing writes the canonical Meta Marketing API payload plus
        a SHA-256 hash of every byte. Once frozen, the package is
        immutable — revisions require a new package with a new hash
        and its own approval chain.
      </p>
      {error && <ErrorBanner error={error} testId="wizard-step-4-error" />}

      {pkg?.frozen_payload_sha256 ? (
        <div className="bo-card" data-testid="freeze-details"
             style={{ background: "var(--bo-panel-alt)" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ fontFamily: "var(--bo-serif)", fontSize: 18 }}>Frozen</div>
            <StateLabel state={pkg.state} />
          </div>
          <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}
               data-testid="frozen-sha256">
            sha256: {pkg.frozen_payload_sha256}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--bo-muted)" }}>
            Objective <strong>{pkg.frozen_payload?.campaign?.objective}</strong>{" · "}
            Daily budget <strong>${pkg.frozen_payload?.ad_set?.daily_budget_usd?.toFixed(2)}</strong>
          </div>
        </div>
      ) : (
        <p style={{ color: "var(--bo-muted)" }}>Not frozen yet.</p>
      )}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between" }}>
        <button className="bo-btn bo-btn-ghost" data-testid="wizard-step-4-back"
                onClick={onBack}>Back</button>
        <div style={{ display: "flex", gap: 8 }}>
          {!pkg?.frozen_payload_sha256 && (
            <button className="bo-btn bo-btn-primary" data-testid="wizard-step-4-freeze"
                    disabled={busy} onClick={onFreeze}>
              {busy ? "Freezing…" : "Freeze package"}
            </button>
          )}
          {pkg?.frozen_payload_sha256 && (
            <button className="bo-btn bo-btn-primary" data-testid="wizard-step-4-next"
                    onClick={onNext}>Continue to publish</button>
          )}
        </div>
      </div>
    </div>
  );
}

function PackageInsightsStrip({ packageId }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/ads/packages/${packageId}/insights`);
        if (alive) setState({ loading: false, data: r.data, error: null });
      } catch (e) {
        if (alive) setState({ loading: false, data: null, error: e?.message || "load_failed" });
      }
    })();
    return () => { alive = false; };
  }, [packageId]);

  if (state.loading) {
    return (
      <div style={{ marginTop: 10, fontSize: 12, color: "var(--bo-muted)" }}
           data-testid="package-insights-loading">Loading live insights…</div>
    );
  }
  if (state.error) return null;
  const d = state.data || {};
  if (!d.available) {
    return (
      <div
        style={{ marginTop: 10, fontSize: 12, color: "var(--bo-muted)" }}
        data-testid="package-insights-unavailable"
      >
        Live insights not available yet ({d.reason || "no data"}). Pull
        insights on the Ads console once Meta has rolled up delivery.
      </div>
    );
  }
  const cell = (label, val, testid) => (
    <div
      key={label}
      style={{ padding: "4px 8px", borderLeft: "2px solid var(--bo-border, #e6e2d8)" }}
      data-testid={testid}
    >
      <div className="bo-eyebrow" style={{ fontSize: 10 }}>{label}</div>
      <div style={{ fontFamily: "var(--bo-serif)", fontSize: 15 }}>{val}</div>
    </div>
  );
  return (
    <div
      style={{
        marginTop: 12, padding: 8, background: "var(--bo-panel-alt, #f6f4ef)",
        borderRadius: 6, display: "flex", gap: 4, flexWrap: "wrap",
      }}
      data-testid="package-insights-strip"
    >
      <div
        className="bo-eyebrow"
        style={{ padding: "4px 8px", fontSize: 11, alignSelf: "center" }}
      >
        Live insights · {d.level}
      </div>
      {cell("Spend", `$${Number(d.spend || 0).toFixed(2)}`, "package-insights-spend")}
      {cell("Impressions", Number(d.impressions || 0).toLocaleString(), "package-insights-imp")}
      {cell("Clicks", Number(d.clicks || 0).toLocaleString(), "package-insights-clicks")}
      {cell("Conversions", d.conversions ?? "—", "package-insights-conv")}
      {cell("CPA", d.cpa != null ? `$${d.cpa}` : "—", "package-insights-cpa")}
    </div>
  );
}

function Step5Publish({ pkg, busy, error, testMode, setTestMode, onPublish,
                        onBack, flags, receipts }) {
  const isPublished = pkg?.state === "published";
  const isFailed = pkg?.state === "failed";
  const receipt = receipts && receipts.length > 0 ? receipts[0] : null;
  return (
    <div className="bo-card" data-testid="wizard-step-5-card">
      <div className="bo-eyebrow">Step 5 · Publish</div>
      <p style={{ color: "var(--bo-muted)", margin: "8px 0 16px" }}>
        Publish invokes the Meta Marketing API adapter. The spend
        governor checks the $25/day cap first — if the cap is
        exceeded the package is refused and the failure is recorded on
        an audit receipt.
      </p>
      {error && <ErrorBanner error={error} testId="wizard-step-5-error" />}

      {!isPublished && !isFailed && (
        <label className="bo-check"
               data-testid="wizard-publish-testmode-toggle"
               style={{ display: "flex", gap: 8, alignItems: "center",
                        marginBottom: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={testMode}
                 onChange={(e) => setTestMode(e.target.checked)}
                 disabled={!flags?.meta_ads_live_enabled} />
          <span>
            <strong>Publish in sandbox / test_mode</strong>{" "}
            <span style={{ color: "var(--bo-muted)" }}>
              (recommended until Meta app perms are approved)
            </span>
          </span>
        </label>
      )}

      {isPublished && receipt && (
        <div className="bo-card" data-testid="publish-result"
             style={{ background: "var(--bo-panel-alt)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontFamily: "var(--bo-serif)", fontSize: 20 }}>
              Published{receipt.test_mode ? " (sandbox)" : " (live)"}
            </div>
            <StateLabel state="published" />
          </div>
          <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 12 }}
               data-testid="publish-external-ids">
            <div>campaign: {receipt.external_refs?.campaign_id}</div>
            <div>adset: {receipt.external_refs?.adset_id}</div>
            <div>ad: {receipt.external_refs?.ad_id}</div>
          </div>
          {receipt.external_artifact_url && (
            <div style={{ marginTop: 8 }}>
              <a href={receipt.external_artifact_url} target="_blank" rel="noreferrer"
                 data-testid="publish-external-link">
                Open in Meta Ads Manager →
              </a>
            </div>
          )}
          <PackageInsightsStrip packageId={pkg.id} />
        </div>
      )}

      {isFailed && (
        <div className="bo-card" data-testid="publish-failed"
             style={{ background: "#2a1a10", borderColor: "#8a2b0a", color: "#f5c1b0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontFamily: "var(--bo-serif)", fontSize: 18 }}>Publish refused</div>
            <StateLabel state="failed" />
          </div>
          <div style={{ marginTop: 8, fontSize: 13 }}>
            <strong>Code:</strong> {pkg.last_error?.code}
          </div>
          <div style={{ marginTop: 4, fontSize: 13 }}>
            {pkg.last_error?.message}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between" }}>
        <button className="bo-btn bo-btn-ghost" data-testid="wizard-step-5-back"
                onClick={onBack}>Back</button>
        {!isPublished && !isFailed && (
          <button className="bo-btn bo-btn-primary"
                  data-testid="wizard-step-5-publish"
                  disabled={busy} onClick={onPublish}>
            {busy ? "Publishing…" : (testMode ? "Publish (sandbox)" : "Publish LIVE")}
          </button>
        )}
        {(isPublished || isFailed) && (
          <Link to="/brand-os/ads" className="bo-btn bo-btn-primary"
                data-testid="wizard-return-to-accounts">
            Return to Ad Accounts
          </Link>
        )}
      </div>
    </div>
  );
}

export default function AdCreationWizard() {
  const [step, setStep] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [flags, setFlags] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [testMode, setTestMode] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    campaign_name: "",
    objective: "OUTCOME_LEADS",
    daily_budget_usd: "5.00",
    ad_copy: "",
    headline: "",
    cta: "LEARN_MORE",
    product_id: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [a, f] = await Promise.all([
          api.get("/ads/ad-accounts"),
          api.get("/ads/feature-flags"),
        ]);
        setAccounts(a.data.ad_accounts || []);
        setFlags(f.data);
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
  }, []);

  async function saveDraftAndPreflight() {
    setBusy(true); setError(null);
    try {
      const create = await api.post("/ads/packages", {
        ad_account_id: selectedAccount,
        campaign_name: form.campaign_name.trim(),
        objective: form.objective,
        daily_budget_usd: parseFloat(form.daily_budget_usd || "0"),
        ad_copy: form.ad_copy.trim(),
        headline: form.headline.trim(),
        cta: form.cta,
        product_id: form.product_id.trim() || undefined,
      });
      const pid = create.data.id;
      const pf = await api.post(`/ads/packages/${pid}/preflight`);
      setPkg(pf.data);
      setStep(2);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function rerunPreflight() {
    if (!pkg) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/ads/packages/${pkg.id}/preflight`);
      setPkg(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function freezeNow() {
    if (!pkg) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/ads/packages/${pkg.id}/freeze`);
      setPkg(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function publishNow() {
    if (!pkg) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/ads/packages/${pkg.id}/publish`, { test_mode: testMode });
      setPkg(r.data);
      const rec = await api.get(`/ads/packages/${pkg.id}/receipts`);
      setReceipts(rec.data.receipts || []);
    } catch (err) {
      setError(formatApiError(err));
      // Refresh package to see failure state
      try {
        const r = await api.get(`/ads/packages/${pkg.id}`);
        setPkg(r.data);
        const rec = await api.get(`/ads/packages/${pkg.id}/receipts`);
        setReceipts(rec.data.receipts || []);
      } catch {}
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <div className="bo-page" data-testid="ad-wizard-page">
        <header className="bo-page-header">
          <div>
            <div className="bo-eyebrow">PAID ADS · CREATE</div>
            <h1 data-testid="ad-wizard-title">New Ad Package</h1>
            <p className="bo-page-lede">
              Compose, preflight, freeze, publish. Same governance rail
              the organic publishing flow uses.
            </p>
          </div>
          <div className="bo-page-actions">
            <Link to="/brand-os/ads" className="bo-btn bo-btn-ghost"
                  data-testid="ad-wizard-cancel">Cancel</Link>
          </div>
        </header>

        <StepBar activeIdx={step} />

        {step === 0 && (
          <Step1SelectAccount accounts={accounts}
                              value={selectedAccount}
                              onChange={setSelectedAccount}
                              onNext={() => setStep(1)} />
        )}
        {step === 1 && (
          <Step2Compose form={form} onChange={setForm}
                        busy={busy} error={error}
                        onSubmit={saveDraftAndPreflight}
                        onBack={() => { setStep(0); setError(null); }} />
        )}
        {step === 2 && (
          <Step3Preflight pkg={pkg} busy={busy} error={error}
                          onRun={rerunPreflight}
                          onNext={() => { setStep(3); setError(null); }}
                          onBack={() => { setStep(1); setError(null); }} />
        )}
        {step === 3 && (
          <Step4Freeze pkg={pkg} busy={busy} error={error}
                       onFreeze={freezeNow}
                       onNext={() => { setStep(4); setError(null); }}
                       onBack={() => { setStep(2); setError(null); }} />
        )}
        {step === 4 && (
          <Step5Publish pkg={pkg} busy={busy} error={error}
                        testMode={testMode} setTestMode={setTestMode}
                        onPublish={publishNow}
                        onBack={() => { setStep(3); setError(null); }}
                        flags={flags} receipts={receipts} />
        )}
      </div>
    </Shell>
  );
}
